import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { syncSelection } from './selection.ts';
import type { Command } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { COLORS, FONT_FAMILIES, FONT_SIZES, fontName, markValue, setMarkValue, valuesInDoc, type ValueMark } from './commands.ts';

/**
 * The toolbar's font, size, text colour and highlight controls
 * (docs/ENTRY-EDITOR-SLICE-3-PLAN.md section 3.5). Each shows what's at the cursor, or
 * "Mixed" for a selection with several; a value already in the text that isn't on a list
 * (a font from the desktop, say) is offered too, so it can be seen and kept.
 */

const MIXED = '__mixed__';
const NONE = '';

export function FormatControls({ view }: { view: EditorView }) {
  const state = view.state;
  const run = (command: Command) => {
    syncSelection(view);
    command(view.state, view.dispatch);
    view.focus();
  };
  // Fonts and sizes in this entry's text, worked out again only when the text changes.
  const doc = state.doc;
  const fontsInText = useMemo(() => valuesInDoc(doc, 'font_family'), [doc]);
  const sizesInText = useMemo(() => valuesInDoc(doc, 'font_size'), [doc]);

  const family = markValue(state, 'font_family');
  const families = [...FONT_FAMILIES.map(([, css]) => css), ...fontsInText.filter((f) => !FONT_FAMILIES.some(([, css]) => css === f))];
  if (typeof family === 'string' && family !== 'mixed' && !families.includes(family)) families.push(family);

  const size = markValue(state, 'font_size');
  // `medium` is the desktop's 12 pt, the same as no size.
  const sizeShown = size === 'medium' ? NONE : size === 'mixed' ? MIXED : (size ?? NONE);
  const extraSizes = [...sizesInText, ...(typeof size === 'string' && size !== 'mixed' ? [size] : [])].filter(
    (s, i, all) => s !== 'medium' && !FONT_SIZES.some(([value]) => value === s) && all.indexOf(s) === i,
  );

  return (
    <span className="toolbar-group">
      <select
        aria-label="Font"
        className="font-select"
        value={family === 'mixed' ? MIXED : (family ?? NONE)}
        onChange={(event) => {
          const value = event.currentTarget.value;
          if (value !== MIXED) run(setMarkValue('font_family', value === NONE ? null : value));
        }}
      >
        {family === 'mixed' && <option value={MIXED}>Mixed fonts</option>}
        <option value={NONE}>Default font</option>
        {families.map((css) => (
          <option key={css} value={css}>
            {fontName(css)}
          </option>
        ))}
      </select>
      <select
        aria-label="Size"
        value={sizeShown}
        onChange={(event) => {
          const value = event.currentTarget.value;
          if (value !== MIXED) run(setMarkValue('font_size', value === NONE ? null : value));
        }}
      >
        {sizeShown === MIXED && <option value={MIXED}>Mixed sizes</option>}
        {FONT_SIZES.map(([value, label]) => (
          <option key={label} value={value ?? NONE}>
            {label}
          </option>
        ))}
        {extraSizes.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <ColorMenu view={view} mark="text_color" label="Text colour" glyph="A" />
      <ColorMenu view={view} mark="background_color" label="Highlight" glyph="ab" />
    </span>
  );
}

/** A button showing the current colour, opening the 16-colour palette and None. */
function ColorMenu({ view, mark, label, glyph }: { view: EditorView; mark: ValueMark; label: string; glyph: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const paletteId = useId();
  const current = markValue(view.state, mark);
  const currentName = current === null ? 'none' : current === 'mixed' ? 'mixed' : (COLORS.find(([, rgb]) => rgb === current)?.[0] ?? current);

  // Kept on screen: a palette opened near the right edge (a phone, say) moves left to fit.
  const palette = useRef<HTMLSpanElement>(null);
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    if (!open) {
      setShift(0); // measured afresh, unshifted, the next time it opens
      return;
    }
    if (!palette.current) return;
    const rect = palette.current.getBoundingClientRect();
    // Within the editor where there is one, and the window anyway.
    const editor = palette.current.closest('.rich-text-editor')?.getBoundingClientRect();
    const right = Math.min(editor?.right ?? Infinity, document.documentElement.clientWidth - 8);
    const left = Math.max(editor?.left ?? 0, 8);
    const over = rect.right - right;
    setShift(over > 0 ? -Math.min(over, rect.left - left) : 0);
  }, [open]);

  // Closes on a click elsewhere, and on Escape (handled on the palette).
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  function choose(value: string | null) {
    setOpen(false);
    syncSelection(view);
    setMarkValue(mark, value)(view.state, view.dispatch);
    view.focus();
  }

  const swatch = typeof current === 'string' && current !== 'mixed' ? current : 'transparent';
  return (
    <span className="color-menu" ref={box}>
      <button
        type="button"
        className="tool"
        title={`${label}: ${currentName}`}
        aria-label={`${label}: ${currentName}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={paletteId}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen(!open)}
      >
        {/* A letter over a colour bar, and an arrow: the usual colour button, so it reads as one. */}
        <span className="color-button-face">
          <span className={mark === 'text_color' ? 'color-glyph' : 'color-glyph highlight'}>{glyph}</span>
          {/* No colour (or several): a rainbow bar, saying "colours". */}
          <span className={swatch === 'transparent' ? 'color-bar unset' : 'color-bar'} style={swatch === 'transparent' ? undefined : { backgroundColor: swatch }} />
        </span>
        <span className="color-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <span
          id={paletteId}
          ref={palette}
          style={{ left: shift }}
          className="color-palette"
          role="group"
          aria-label={label}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setOpen(false);
              view.focus();
            }
          }}
        >
          {COLORS.map(([name, rgb], i) => (
            <button
              key={rgb}
              type="button"
              className="swatch"
              style={{ backgroundColor: rgb }}
              title={name}
              aria-label={name}
              aria-pressed={current === rgb}
              autoFocus={current === rgb || (i === 0 && !COLORS.some(([, c]) => c === current))}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(rgb)}
            />
          ))}
          <button type="button" className="swatch-none" onMouseDown={(event) => event.preventDefault()} onClick={() => choose(null)}>
            None
          </button>
        </span>
      )}
    </span>
  );
}
